import { Group, VolunteerActivism } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { updateParticipants, setIntegratedCarePlan, updateNarrative, updateIsReviewed, getCarePlanSignatureStatus } from '../../../store/carePlanUpdate.slice';
import { setEngagementDraft } from '../../../store/documentEngagement.slice';
import { copyObject } from '../../../utils/common';
import { useParams } from 'react-router-dom';
import { CarePlanSignatureStatusEnum } from '../../../Enum/CarePlanSignatureStatusEnum';
import { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/CarePlans.pdf';

interface CarePlanTopSectionProps {
    isAssessment?: boolean;
}

const CarePlanTopSection: React.FC<CarePlanTopSectionProps> = ({ isAssessment = false }) => {
    const { carePlanSignatureStatus, integratedCarePlan } = useSelector((state: RootState) => state.carePlanUpdate);
    const { engagementDraft, isSaving } = useSelector((state: RootState) => state.documentEngagement);
    const dispatch = useAppDispatch();
    const params = useParams();
    const memberId = params.memberId || '';

    const reviewDetail = Boolean(integratedCarePlan?.ReviewHistory?.length) ? integratedCarePlan?.ReviewHistory[0] : null;
    const reviewDetailMember = Boolean(integratedCarePlan?.ReviewHistoryMember?.length) ? integratedCarePlan?.ReviewHistoryMember[0] : null;
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const isReviewed = Boolean(isAssessment ? engagementDraft.IsCarePlanReviewed : integratedCarePlan.IsReviewed);
    const reviewedOn = isAssessment ? engagementDraft.CarePlanReviewedOn : integratedCarePlan.IsReviewedDate;

    const [isChanged, setIsChanged] = useState(false);
    const [showReviewConfirmation, setShowReviewConfirmation] = useState(false);

    const participantOptions = ['Member', 'ChpProvider', 'Guardian', 'Family', 'BehavioralHealthHome', 'PeerSupport', 'Other'];
    const participantLabels: Record<string, string> = {
        ChpProvider: 'CHP Provider',
        BehavioralHealthHome: 'Behavioral Health Home',
        PeerSupport: 'Peer Support',
    };
    const handleParticipantChange = (participant: string, event: React.ChangeEvent<HTMLInputElement>) => {
        const carePlanUpdate = copyObject(integratedCarePlan);
        carePlanUpdate.Participants[participant] = event.target.checked;
        dispatch(updateParticipants(memberId, carePlanUpdate.Participants));
    };

    const handleSaveNarrative = () => {
        dispatch(updateNarrative(memberId, integratedCarePlan.Narrative));
        setIsChanged(false);
    };

    const updateReviewStatus = (reviewed: boolean) => {
        if (isAssessment) {
            dispatch(
                setEngagementDraft({
                    ...engagementDraft,
                    IsCarePlanReviewed: reviewed,
                    CarePlanReviewedOn: reviewed ? new Date().toISOString() : null,
                })
            );
        } else {
            dispatch(updateIsReviewed(memberId, reviewed));
        }
    };

    const handleReviewCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            // Show confirmation modal before marking as reviewed
            setShowReviewConfirmation(true);
        } else {
            updateReviewStatus(false);
        }
    };

    const handleConfirmReview = () => {
        updateReviewStatus(true);
        setShowReviewConfirmation(false);
    };

    const handleCancelReview = () => {
        setShowReviewConfirmation(false);
    };

    useEffect(() => {
        dispatch(getCarePlanSignatureStatus(memberId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberId]);

    return (
        <Box>
            {/* 1. Header: Member & Session Info */}
            <Box sx={{ mb: 2, p: { xs: 1, sm: 2 }, border: '1px solid #e3e6f0', borderRadius: 2, bgcolor: '#fff' }}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'center'}>
                    <Typography variant="h6" component="div">
                        Care Plan
                    </Typography>
                    <Tooltip title="Care Plans Help Document">
                        <InfoIcon
                            sx={{ marginLeft: 1 }}
                            color="primary"
                            fontSize="small"
                            cursor="pointer"
                            onClick={() => window.open(`${HelpPage}`, '_blank')}
                        />
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
                    <Stack direction={'row'} spacing={4} alignItems="center">
                        <Typography variant="subtitle1" color="text.secondary">
                            <strong>Last Updated:</strong>{' '}
                            {integratedCarePlan?.LastUpdatedTime
                                ? (() => {
                                      const d = toDate(integratedCarePlan?.LastUpdatedTime);
                                      return d && d.getFullYear() > 1900 ? formatDate(d, 'MM/dd/yyyy') : '—';
                                  })()
                                : '—'}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} spacing={4} alignItems="center">
                        <Typography variant="subtitle1" color="text.secondary">
                            <strong>Last Reviewed By:</strong> {`${reviewDetail ? reviewDetail?.LastReviewedBy?.Name + ', ' : '—'} `}
                            {reviewDetail?.LastReviewedOn
                                ? (() => {
                                      const d = toDate(reviewDetail?.LastReviewedOn);
                                      return d && d.getFullYear() > 1900 ? formatDate(d, 'MM/dd/yyyy') : '';
                                  })()
                                : null}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} spacing={4} alignItems="flex-start">
                        <Stack spacing={0} alignItems={'center'} justifyContent={'center'}>
                            <Typography variant="subtitle2" textAlign={'center'}>
                                Provider Signature
                            </Typography>
                            <Chip
                                label={carePlanSignatureStatus.ProviderSignature.SignatureStatus || ''}
                                color={carePlanSignatureStatus?.ProviderSignature.SignatureStatus === 'Complete' ? 'success' : 'error'}
                                size="small"
                            />
                            {reviewDetail && reviewDetail.Signature ? (
                                <Typography variant="subtitle2" fontStyle={'italic'} color={'text.secondary'}>
                                    {reviewDetail.LastReviewedOn
                                        ? (() => {
                                              const d = toDate(reviewDetail.LastReviewedOn);
                                              return d && d.getFullYear() > 1900 ? formatDate(d, 'MM/dd/yyyy') : '—';
                                          })()
                                        : '—'}
                                </Typography>
                            ) : null}
                        </Stack>
                        <Stack spacing={0} alignItems={'center'} justifyContent={'center'}>
                            <Typography variant="subtitle2" textAlign={'center'}>
                                Member Signature
                            </Typography>
                            {!integratedCarePlan.ShareWithMember && (
                                <Chip
                                    label={carePlanSignatureStatus?.MemberSignature.SignatureStatus || ''}
                                    color={carePlanSignatureStatus?.MemberSignature.SignatureStatus === 'Complete' ? 'success' : 'error'}
                                    size="small"
                                />
                            )}
                            {integratedCarePlan.ShareWithMember && carePlanSignatureStatus?.MemberSignature.SignatureStatus !== 'Complete' && (
                                <Chip label={CarePlanSignatureStatusEnum.PendingDescription} color={'warning'} size="small" />
                            )}
                            {!integratedCarePlan.ShareWithMember && reviewDetailMember && reviewDetailMember.Signature ? (
                                <Typography variant="subtitle2" fontStyle={'italic'} color={'text.secondary'}>
                                    {reviewDetailMember.LastReviewedOn
                                        ? (() => {
                                              const d = toDate(reviewDetailMember.LastReviewedOn);
                                              return d && d.getFullYear() > 1900 ? formatDate(d, 'MM/dd/yyyy') : '—';
                                          })()
                                        : '—'}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
            {/* 1.5 Reviewed with Member Checkbox */}
            {(isAssessment || (isReviewed && reviewedOn) || carePlanSignatureStatus?.MemberSignature.SignatureStatus === 'Complete') && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid #e3e6f0', borderRadius: 2, bgcolor: '#fff' }}>
                    {isAssessment && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isReviewed}
                                    disabled={isLoading || (isAssessment && isSaving) || integratedCarePlan.Goals.length === 0}
                                    onChange={handleReviewCheckboxChange}
                                />
                            }
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography>Reviewed with Member</Typography>
                                    <Tooltip title="Check this box to mark the care plan as reviewed with the member for this assessment.">
                                        <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                </Stack>
                            }
                        />
                    )}
                    {isReviewed && reviewedOn && (isAssessment || carePlanSignatureStatus?.MemberSignature.SignatureStatus !== 'Complete') && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                            Marked as reviewed on:{' '}
                            {(() => {
                                const d = toDate(reviewedOn);
                                return d ? formatDate(d, 'MM/dd/yyyy hh:mm a') : '—';
                            })()}
                        </Typography>
                    )}
                    {!isAssessment && carePlanSignatureStatus?.MemberSignature.SignatureStatus === 'Complete' && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                            Member signature received - automatically marked as reviewed
                        </Typography>
                    )}
                </Box>
            )}
            {/* 2. Participants */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e3e6f0', borderRadius: 2, bgcolor: '#fff' }}>
                <Typography variant="subtitle1" color="primary" fontWeight={600} mb={1}>
                    <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Individuals Participating in Care Plan
                </Typography>
                <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                    {participantOptions.map((participant) => (
                        <FormControlLabel
                            key={participant}
                            control={
                                <Checkbox checked={integratedCarePlan.Participants[participant]} onChange={(e) => handleParticipantChange(participant, e)} />
                            }
                            label={participantLabels[participant] || participant}
                        />
                    ))}
                </Stack>
                {/* Participants Other Note */}
                {integratedCarePlan.Participants?.Other && (
                    <Box sx={{ mt: 2, ml: 2 }}>
                        <TextField
                            label="Please specify (Other Participant)"
                            value={integratedCarePlan.Participants.OtherNote || ''}
                            onChange={(e) =>
                                dispatch(
                                    setIntegratedCarePlan({
                                        ...integratedCarePlan,
                                        Participants: {
                                            ...integratedCarePlan.Participants,
                                            OtherNote: e.target.value,
                                        },
                                    })
                                )
                            }
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}
            </Box>

            {/* Confirmation Modal for Review */}
            <Dialog open={isAssessment && showReviewConfirmation} onClose={handleCancelReview} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Care Plan Review</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to mark this care plan as reviewed with the member?
                        <br />
                        <br />
                        This action will record that you have reviewed the care plan with the member without requiring their signature.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelReview} color="primary" variant="outlined" sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmReview} variant="contained" color="primary" autoFocus>
                        Confirm Review
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 4. Narrative Section */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e3e6f0', borderRadius: 2, bgcolor: '#fff' }}>
                <Typography variant="subtitle1" color="secondary" fontWeight={600}>
                    <VolunteerActivism sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Recovery Goal / Person-Family Vision
                </Typography>
                <TextField
                    multiline
                    minRows={2}
                    fullWidth
                    placeholder="Enter narrative in the member's own words..."
                    sx={{ mt: 1 }}
                    value={integratedCarePlan.Narrative || ''}
                    onChange={(e) => {
                        dispatch(setIntegratedCarePlan({ ...integratedCarePlan, Narrative: e.target.value }));
                        setIsChanged(true);
                    }}
                />
                <Button variant="contained" color="primary" sx={{ mt: 2, minWidth: '120px', ml: 'auto' }} onClick={handleSaveNarrative} disabled={!isChanged}>
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default CarePlanTopSection;
